import React, { useEffect, useState, useContext, useCallback } from 'react';
import {
  Table, Button, Modal, Form, DatePicker, Select, Input, Space,
  Typography, Popconfirm, message, Tag, Tooltip, Card, Segmented, Spin,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { UserContext } from '../App';
import api from '../api';

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const DEFAULT_FROM = dayjs().startOf('year');
const DEFAULT_TO   = dayjs();

const RACE_COLORS = [
  '#e8682a', '#d4a017', '#8b5e3c', '#2563eb',
  '#7c3aed', '#059669', '#dc2626', '#0891b2', '#c026d3', '#4f46e5',
];
const MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };

function CheersHorseRace({ agents }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setReady(true), 60);
    return () => clearTimeout(t);
  }, []);

  if (!agents || !agents.length) return null;

  const maxCnt = Math.max(...agents.map((a) => a.Cnt));

  const barPct = (cnt) => {
    if (maxCnt === 0) return 0;
    return Math.round((cnt / maxCnt) * 80 + 20);
  };

  return (
    <Card title="Most Cheers — Top 10 Agents" size="small" style={{ marginBottom: 16 }}>
      <style>{`
        @keyframes cheerLanePop {
          from { opacity: 0; transform: translateX(-12px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div style={{
        background: 'linear-gradient(180deg, #1c1917 0%, #292524 100%)',
        borderRadius: 12,
        padding: '16px 20px 20px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
        overflow: 'hidden',
      }}>
        <div style={{
          color: 'rgba(255,255,255,0.45)', fontSize: 11, fontWeight: 700,
          letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 16,
        }}>
          Top {agents.length}
        </div>

        {agents.map((agent, i) => {
          const rank    = i + 1;
          const pct     = barPct(agent.Cnt);
          const color   = RACE_COLORS[i % RACE_COLORS.length];
          const isFirst = rank === 1;
          return (
            <div
              key={agent.AgentName}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
                animation: `cheerLanePop 0.35s ease-out ${i * 0.06}s both`,
              }}
            >
              {/* Rank badge */}
              <div style={{
                width: 32, flexShrink: 0, textAlign: 'right',
                fontSize: rank <= 3 ? 18 : 13, fontWeight: 700, lineHeight: 1,
              }}>
                {MEDAL[rank] ?? <span style={{ color: 'rgba(255,255,255,0.45)' }}>#{rank}</span>}
              </div>

              {/* Name + team */}
              <div style={{
                width: 150, flexShrink: 0,
                color: isFirst ? '#fde68a' : 'rgba(255,255,255,0.82)',
                fontSize: 13, fontWeight: isFirst ? 700 : 500,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {agent.AgentName}
                {agent.TeamName && (
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>
                    {agent.TeamName}
                  </div>
                )}
              </div>

              {/* Bar track */}
              <div style={{
                flex: 1, height: 32, background: 'rgba(255,255,255,0.06)',
                borderRadius: 6, overflow: 'hidden', position: 'relative',
              }}>
                {[25, 50, 75].map((mark) => (
                  <div key={mark} style={{
                    position: 'absolute', top: 0, bottom: 0,
                    left: `${mark}%`, width: 1,
                    background: 'rgba(255,255,255,0.06)',
                  }} />
                ))}
                <div style={{
                  height: '100%',
                  width: ready ? `${pct}%` : '0%',
                  background: `linear-gradient(90deg, ${color}cc, ${color})`,
                  borderRadius: 6,
                  transition: `width ${0.7 + i * 0.04}s cubic-bezier(0.22, 1, 0.36, 1) ${i * 0.07}s`,
                  display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                  paddingRight: 10, boxSizing: 'border-box',
                  boxShadow: `0 0 12px ${color}66`,
                }}>
                  <Text style={{
                    color: '#fff', fontSize: 12, fontWeight: 700,
                    whiteSpace: 'nowrap', textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                  }}>
                    {agent.Cnt} cheers
                  </Text>
                </div>
              </div>

              {isFirst && <Text style={{ fontSize: 16, flexShrink: 0 }}>🏁</Text>}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function useFetch(url) {
  const [data, setData] = useState([]);
  const reload = useCallback(() => {
    api.get(url).then(r => setData(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [url]);
  useEffect(() => { reload(); }, [reload]);
  return [data, reload];
}

// ---------------------------------------------------------------------------
// Pivot table for Stats view
// ---------------------------------------------------------------------------
function SimplePivotCard({ title, data, valueKey, valueLabel }) {
  const months = [...new Set(data.map(r => r.Month))].sort();

  const grouped = {};
  data.forEach(r => {
    const k = r[valueKey];
    if (!grouped[k]) grouped[k] = { val: k };
    grouped[k][r.Month] = (grouped[k][r.Month] || 0) + r.Cnt;
  });
  const tableRows = Object.values(grouped).sort((a, b) => a.val.localeCompare(b.val));

  const grandTotals = {};
  months.forEach(m => { grandTotals[m] = 0; });
  tableRows.forEach(r => { months.forEach(m => { grandTotals[m] += r[m] || 0; }); });

  const columns = [
    { title: valueLabel, dataIndex: 'val', key: 'val', fixed: 'left', width: 150, ellipsis: true },
    ...months.map(m => ({
      title: m, dataIndex: m, key: m, align: 'right', width: 78,
      render: v => v != null && v > 0 ? v : <Text type="secondary">-</Text>,
    })),
  ];

  return (
    <Card title={title} size="small" style={{ marginBottom: 16 }}>
      <Table
        rowKey="val"
        dataSource={tableRows}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        summary={() => (
          <Table.Summary.Row style={{ fontWeight: 700 }}>
            <Table.Summary.Cell index={0}>Grand Total</Table.Summary.Cell>
            {months.map((m, i) => (
              <Table.Summary.Cell key={m} index={i + 1} align="right">
                {grandTotals[m] > 0 ? grandTotals[m] : '-'}
              </Table.Summary.Cell>
            ))}
          </Table.Summary.Row>
        )}
      />
    </Card>
  );
}

function PivotCard({ title, data, valueKey, valueLabel }) {
  const months = [...new Set(data.map(r => r.Month))].sort();

  // Build detail rows
  const grouped = {};
  data.forEach(r => {
    const k = `${r.Team}||${r[valueKey]}`;
    if (!grouped[k]) grouped[k] = { team: r.Team, val: r[valueKey] };
    grouped[k][r.Month] = r.Cnt;
  });
  const detailRows = Object.values(grouped).sort((a, b) =>
    a.team.localeCompare(b.team) || a.val.localeCompare(b.val)
  );

  // Inject a subtotal row after each team's detail rows
  const teams = [...new Set(detailRows.map(r => r.team))];
  const tableRows = [];
  const grandTotals = {};
  months.forEach(m => { grandTotals[m] = 0; });

  teams.forEach(team => {
    const teamRows = detailRows.filter(r => r.team === team);
    const teamTotals = {};
    months.forEach(m => { teamTotals[m] = 0; });
    teamRows.forEach(r => {
      months.forEach(m => {
        teamTotals[m] += r[m] || 0;
        grandTotals[m] += r[m] || 0;
      });
      tableRows.push(r);
    });
    tableRows.push({ _key: `__total__${team}`, team, val: 'Total', _isTeamTotal: true, ...teamTotals });
  });

  const columns = [
    { title: 'Team',     dataIndex: 'team', key: 'team', fixed: 'left', width: 130, ellipsis: true,
      render: (v, r) => r._isTeamTotal ? <Text strong>{v}</Text> : v },
    { title: valueLabel, dataIndex: 'val',  key: 'val',  fixed: 'left', width: 150, ellipsis: true,
      render: (v, r) => r._isTeamTotal ? <Text strong>{v}</Text> : v },
    ...months.map((m, colIdx) => ({
      title: m, dataIndex: m, key: m, align: 'right', width: 78,
      render: (v, r) => {
        if (r._isTeamTotal) return <Text strong>{v > 0 ? v : '-'}</Text>;
        return v != null && v > 0 ? v : <Text type="secondary">-</Text>;
      },
    })),
  ];

  return (
    <Card title={title} size="small" style={{ marginBottom: 16 }}>
      <Table
        rowKey={(r, i) => r._key ?? `${r.team}|${r.val}`}
        dataSource={tableRows}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        onRow={r => ({
          style: r._isTeamTotal
            ? { fontWeight: 600, background: 'var(--ant-color-fill-quaternary)' }
            : {},
        })}
        summary={() => (
          <Table.Summary.Row style={{ fontWeight: 700 }}>
            <Table.Summary.Cell index={0} colSpan={2}>Grand Total</Table.Summary.Cell>
            {months.map((m, i) => (
              <Table.Summary.Cell key={m} index={i + 2} align="right">
                {grandTotals[m] > 0 ? grandTotals[m] : '-'}
              </Table.Summary.Cell>
            ))}
          </Table.Summary.Row>
        )}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Stats view
// ---------------------------------------------------------------------------
function StatsView({ stats, loading }) {
  if (loading) return <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin size="large" /></div>;
  if (!stats)  return null;

  return (
    <>
      <CheersHorseRace agents={stats.topAgents} />
      <SimplePivotCard
        title="Cheers by Month — Source"
        data={stats.bySource}
        valueKey="Source"
        valueLabel="Source"
      />
      <SimplePivotCard
        title="Cheers by Month — Category"
        data={stats.byCategory}
        valueKey="Category"
        valueLabel="Category"
      />
      <SimplePivotCard
        title="Cheers by Month — Team"
        data={stats.byCategory}
        valueKey="Team"
        valueLabel="Team"
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function CheersPage() {
  const currentUser = useContext(UserContext);

  // Filters
  const [dateRange,     setDateRange]     = useState([DEFAULT_FROM, DEFAULT_TO]);
  const [teamFilter,    setTeamFilter]    = useState(null);
  const [sourceFilter,  setSourceFilter]  = useState(null);
  const [categoryFilter,setCategoryFilter]= useState(null);
  const [viewMode, setViewMode]           = useState('Stats');

  // List state
  const [cheers, setCheers]       = useState([]);
  const [listLoading, setListLoading] = useState(false);

  // Stats state
  const [stats, setStats]           = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Dropdown data
  const [teams]      = useFetch('/api/cheers/teams-list');
  const [agents]     = useFetch('/api/users');
  const [sources]    = useFetch('/api/cheers/sources');
  const [categories] = useFetch('/api/cheers/categories');

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [form]                    = Form.useForm();

  const dateParams = {
    dateFrom: dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
    dateTo:   dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
  };

  const loadCheers = useCallback(() => {
    setListLoading(true);
    const params = { ...dateParams };
    if (teamFilter) params.teamId = teamFilter;
    api.get('/api/cheers', { params })
      .then(r => { setCheers(Array.isArray(r.data) ? r.data : []); setListLoading(false); })
      .catch(() => setListLoading(false));
  }, [teamFilter, dateRange]); // eslint-disable-line

  const loadStats = useCallback(() => {
    setStatsLoading(true);
    const p = { ...dateParams };
    if (sourceFilter)   p.sourceId   = sourceFilter;
    if (categoryFilter) p.categoryId = categoryFilter;
    api.get('/api/cheers/stats', { params: p })
      .then(r => { setStats(r.data); setStatsLoading(false); })
      .catch(() => setStatsLoading(false));
  }, [dateRange, sourceFilter, categoryFilter]); // eslint-disable-line

  useEffect(() => {
    if (viewMode === 'List') loadCheers();
    else loadStats();
  }, [viewMode, loadCheers, loadStats]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldValue('Date', dayjs());
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({
      Date:        row.Date ? dayjs(row.Date) : dayjs(),
      AgentUserID: row.AgentUserID,
      SourceID:    row.SourceID,
      CategoryID:  row.CategoryID,
      Description: row.Description,
      ContactID:   row.ContactID,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let vals;
    try { vals = await form.validateFields(); } catch { return; }
    setSaving(true);
    const body = { ...vals, Date: vals.Date ? vals.Date.format('YYYY-MM-DD') : null };
    try {
      if (editing) await api.put(`/api/cheers/${editing.ID}`, body);
      else         await api.post('/api/cheers', body);
      message.success(editing ? 'Updated.' : 'Cheers created!');
      setModalOpen(false);
      if (viewMode === 'List') loadCheers();
    } catch {
      message.error('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/cheers/${id}`);
      message.success('Deleted.');
      loadCheers();
    } catch {
      message.error('Delete failed.');
    }
  };

  const canEdit = (row) => {
    const role = currentUser?.Role;
    if (role === 'agent') return false;
    return role === 'admin' || role === 'qa_admin' || currentUser?.ID === row.CreatedByUserID;
  };

  const isNonAgent = currentUser?.Role !== 'agent';

  const buildOptions = (list, selectedId) => {
    const active = list.filter(item => item.IsActive);
    if (selectedId && !active.find(item => item.ID === selectedId)) {
      const ghost = list.find(item => item.ID === selectedId);
      if (ghost) return [...active, { ...ghost, _label: `${ghost.Name} (inactive)` }];
    }
    return active;
  };

  const sourceOptions   = buildOptions(sources,    editing?.SourceID).map(s => ({ value: s.ID, label: s._label ?? s.Name }));
  const categoryOptions = buildOptions(categories, editing?.CategoryID).map(c => ({ value: c.ID, label: c._label ?? c.Name }));

  const agentOptions = (() => {
    // Name (Team), as Create Error lists them.
    const base = agents.map(a => ({ value: a.ID, label: a.TeamName ? `${a.DisplayName} (${a.TeamName})` : a.DisplayName }));
    if (editing?.AgentUserID && !agents.find(a => a.ID === editing.AgentUserID)) {
      base.push({ value: editing.AgentUserID, label: `${editing.AgentName ?? editing.AgentUserID} (inactive)` });
    }
    return base;
  })();

  const listColumns = [
    { title: 'Date',        dataIndex: 'Date',         key: 'Date',     width: 100 },
    { title: 'Agent',       dataIndex: 'AgentName',    key: 'Agent',    width: 160, ellipsis: true },
    { title: 'Team',        dataIndex: 'TeamName',     key: 'Team',     width: 140, ellipsis: true,
      render: v => v ? <Tag>{v}</Tag> : null },
    { title: 'Source',      dataIndex: 'SourceName',   key: 'Source',   width: 120, ellipsis: true },
    { title: 'Category',    dataIndex: 'CategoryName', key: 'Category', width: 130, ellipsis: true },
    { title: 'Description', dataIndex: 'Description',  key: 'Desc',     ellipsis: true,
      render: v => <Tooltip title={v}><span>{v}</span></Tooltip> },
    { title: 'Contact ID',  dataIndex: 'ContactID',    key: 'ContactID', width: 120 },
    { title: 'Created By',  dataIndex: 'CreatedByName', key: 'CreatedBy', width: 140, ellipsis: true },
    { title: 'Created',     dataIndex: 'CreatedAt',    key: 'CreatedAt', width: 155,
      render: v => v ? v.slice(0, 16).replace('T', ' ') : '' },
    { title: '', key: 'actions', width: 80, fixed: 'right',
      render: (_, row) => canEdit(row) ? (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)} />
          <Popconfirm title="Delete this entry?" onConfirm={() => handleDelete(row.ID)} okText="Delete" okType="danger">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) : null,
    },
  ];

  const teamOptions = Array.from(
    new Map([...teams].map(t => [t.ID, t.Name])).entries()
  ).map(([id, name]) => ({ value: id, label: name }));

  return (
    <div style={{ padding: '16px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Cheers for Peers</Typography.Title>
        {isNonAgent && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Cheers</Button>
        )}
      </div>

      {/* Filter bar */}
      <Space wrap style={{ marginBottom: 12 }}>
        <RangePicker
          value={dateRange}
          onChange={v => setDateRange(v ?? [null, null])}
          allowClear={false}
        />
        {viewMode === 'List' && (
          <Select
            allowClear
            placeholder="Filter by team"
            style={{ width: 200 }}
            options={teamOptions}
            value={teamFilter}
            onChange={setTeamFilter}
          />
        )}
        {viewMode === 'Stats' && (
          <>
            <Select
              allowClear
              placeholder="All sources"
              style={{ width: 160 }}
              options={sources.filter(s => s.IsActive).map(s => ({ value: s.ID, label: s.Name }))}
              value={sourceFilter}
              onChange={v => setSourceFilter(v ?? null)}
            />
            <Select
              allowClear
              placeholder="All categories"
              style={{ width: 170 }}
              options={categories.filter(c => c.IsActive).map(c => ({ value: c.ID, label: c.Name }))}
              value={categoryFilter}
              onChange={v => setCategoryFilter(v ?? null)}
            />
          </>
        )}
        <Segmented
          options={['Stats', 'List']}
          value={viewMode}
          onChange={setViewMode}
        />
      </Space>

      {/* Content */}
      {viewMode === 'List' ? (
        <Table
          rowKey="ID"
          dataSource={cheers}
          columns={listColumns}
          loading={listLoading}
          size="small"
          pagination={{ defaultPageSize: 25, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      ) : (
        <StatsView stats={stats} loading={statsLoading} />
      )}

      {/* Create / Edit modal */}
      <Modal maskClosable={false}
        title={editing ? 'Edit Cheers for Peers' : 'New Cheers for Peers'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editing ? 'Save' : 'Create'}
        width={520}
        destroyOnClose
      >
        {/* Leads with WebEx, because the misreading this exists to prevent is
            someone treating this form as the place to submit a cheer. Saying
            where they are submitted first, then what this form is for, answers
            that before it can be acted on. */}
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          Cheers for Peers are submitted in <strong>WebEx</strong>. This form is where admin
          staff record them, for reporting and recognition purposes.
        </Typography.Text>

        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="Date" label="Date" rules={[{ required: true, message: 'Date required' }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="AgentUserID" label="Agent" rules={[{ required: true, message: 'Agent required' }]}>
            <Select showSearch placeholder="Select agent" optionFilterProp="label" options={agentOptions} />
          </Form.Item>
          <Form.Item name="SourceID" label="Source">
            <Select allowClear placeholder="Select source" options={sourceOptions} />
          </Form.Item>
          <Form.Item name="CategoryID" label="Category">
            <Select allowClear placeholder="Select category" options={categoryOptions} />
          </Form.Item>
          <Form.Item name="Description" label="Description">
            <TextArea rows={4} placeholder="What did this person do?" />
          </Form.Item>
          <Form.Item name="ContactID" label="Contact ID (optional)">
            <Input placeholder="Contact ID" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
